import { create } from 'zustand'

import AgentsService, {
  type CreateAgentPayload,
  type UpdateAgentPayload,
} from '@/services/agents.service'

export type KnowledgeSourceType = 'text' | 'site' | 'file'
export type AgentRequestMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'

export function createId() {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID()
  }
  return `agent-${Math.random().toString(36).slice(2, 10)}`
}

export interface AgentKnowledgeSource {
  id: string
  type: KnowledgeSourceType
  title: string
  content?: string
  url?: string
  file_name?: string
  mime_type?: string
  size_bytes?: number
  data_url?: string
}

export interface AgentSkillRequestField {
  id: string
  json_name: string
  source_key: string
  required: boolean
}

export interface AgentSkill {
  id: string
  name: string
  quickstart: string
  instructions: string
  request_enabled: boolean
  request_method: AgentRequestMethod
  request_url: string
  request_headers: string
  request_body: string
  request_body_fields: AgentSkillRequestField[]
  reference_source?: AgentKnowledgeSource | null
}

export function migrateLegacyRequestBody(rawBody: string): AgentSkillRequestField[] {
  const text = (rawBody ?? '').trim()
  if (!text) return []
  let parsed: unknown
  try {
    parsed = JSON.parse(text)
  } catch {
    return []
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return []

  const fields: AgentSkillRequestField[] = []
  const seen = new Set<string>()
  for (const [key, value] of Object.entries(parsed as Record<string, unknown>)) {
    if (!key.trim() || seen.has(key)) continue
    if (typeof value !== 'string') continue
    const stripped = value.trim()
    const sourceKey =
      stripped.startsWith('{{') && stripped.endsWith('}}')
        ? stripped.slice(2, -2).trim()
        : stripped
    seen.add(key)
    fields.push({
      id: createId(),
      json_name: key,
      source_key: sourceKey,
      required: false,
    })
  }
  return fields
}

function normalizeSkill(skill: AgentSkill): AgentSkill {
  const fields = Array.isArray(skill.request_body_fields) ? skill.request_body_fields : []
  const normalizedFields: AgentSkillRequestField[] = fields.length
    ? fields.map((field) => ({
        id: field.id || createId(),
        json_name: field.json_name ?? '',
        source_key: field.source_key ?? '',
        required: Boolean(field.required),
      }))
    : migrateLegacyRequestBody(skill.request_body ?? '')
  return {
    ...skill,
    request_body: skill.request_body ?? '',
    request_body_fields: normalizedFields,
  }
}

export interface AgentSubAgent {
  id: string
  name: string
  objective: string
  prompt: string
  personality: string
  knowledge_base: string
  skills: AgentSkill[]
  active: boolean
}

export type FollowUpFlowStatus = 'active' | 'paused' | 'disabled'
export type LastMessageDirection = 'outbound' | 'inbound' | 'any'
export type ConversationStatusFilter = 'open' | 'waiting' | 'any'

export interface FollowUpRuleConfig {
  leadWithoutResponseForMinutes: number
  lastMessageDirection: LastMessageDirection
  conversationStatus: ConversationStatusFilter
  contactLimit: number
  onlyBusinessHours: boolean
  businessHoursStart: string
  businessHoursEnd: string
}

export interface AgentFollowUp {
  id: string
  title: string
  delay_minutes: number
  instructions: string
  active: boolean
}

export interface FollowUpWebhookConfig {
  enabled: boolean
  endpoint: string
  method: 'POST'
  bodyTemplate: string
  headers: string | Record<string, string>
  failStrategy: 'continue' | 'stop'
}

export const DEFAULT_FOLLOWUP_WEBHOOK_BODY_TEMPLATE =
  '{\n  "telefone": "{{telefone}}",\n  "mensagem": "{{mensagem}}"\n}'

export const DEFAULT_FOLLOWUP_WEBHOOK_URL =
  'https://n8n.alcateia-ia.com/webhook/ia/message/whatsapp'

export const DEFAULT_FOLLOWUP_WEBHOOK_CONFIG: FollowUpWebhookConfig = {
  enabled: true,
  endpoint: DEFAULT_FOLLOWUP_WEBHOOK_URL,
  method: 'POST',
  bodyTemplate: DEFAULT_FOLLOWUP_WEBHOOK_BODY_TEMPLATE,
  headers: '',
  failStrategy: 'continue',
}

export interface AgentMetadata {
  follow_up_status?: FollowUpFlowStatus
  follow_up_rule_config?: Partial<FollowUpRuleConfig>
  follow_up_webhook_config?: Partial<FollowUpWebhookConfig>
  follow_up_instance_ids?: string[]
  [key: string]: unknown
}

export interface AgentDefinition {
  id: string
  name: string
  supervisor_name: string
  persona_name: string
  personality: string
  system_prompt: string
  knowledge_base: string
  knowledge_sources: AgentKnowledgeSource[]
  enabled: boolean
  memory_enabled: boolean
  strict_logic_flow: boolean
  followups: AgentFollowUp[]
  subagents: AgentSubAgent[]
  metadata?: AgentMetadata
  created_at: string
  updated_at: string
}

interface AgentCatalogStore {
  agents: AgentDefinition[]
  isLoading: boolean
  fetchAgents: () => Promise<AgentDefinition[]>
  createAgent: (payload: CreateAgentPayload) => Promise<AgentDefinition>
  updateAgent: (agentId: string, payload: UpdateAgentPayload) => Promise<AgentDefinition>
  deleteAgent: (agentId: string) => Promise<void>
}

function normalizeAgent(agent: AgentDefinition): AgentDefinition {
  return {
    ...agent,
    knowledge_sources: Array.isArray(agent.knowledge_sources) ? agent.knowledge_sources : [],
    followups: Array.isArray(agent.followups) ? agent.followups : [],
    subagents: Array.isArray(agent.subagents)
      ? agent.subagents.map((subagent) => ({
          ...subagent,
          active: (subagent as AgentSubAgent & { active?: boolean }).active ?? true,
          skills: Array.isArray(subagent.skills)
            ? subagent.skills.map((skill) => normalizeSkill(skill))
            : [],
        }))
      : [],
    metadata:
      agent.metadata && typeof agent.metadata === 'object' && !Array.isArray(agent.metadata)
        ? agent.metadata
        : {},
    strict_logic_flow:
      (agent as AgentDefinition & { strict_logic_flow?: boolean }).strict_logic_flow ?? true,
  }
}

export const useAgentCatalogStore = create<AgentCatalogStore>((set) => ({
  agents: [],
  isLoading: false,

  fetchAgents: async () => {
    set({ isLoading: true })
    try {
      const result = await AgentsService.list()
      const agents = result.map(normalizeAgent)
      set({ agents })
      return agents
    } finally {
      set({ isLoading: false })
    }
  },

  createAgent: async (payload) => {
    const created = normalizeAgent(await AgentsService.create(payload))
    set((state) => ({ agents: [created, ...state.agents.filter((a) => a.id !== created.id)] }))
    return created
  },

  updateAgent: async (agentId, payload) => {
    const updated = normalizeAgent(await AgentsService.update(agentId, payload))
    set((state) => ({
      agents: state.agents.map((agent) => (agent.id === updated.id ? updated : agent)),
    }))
    return updated
  },

  deleteAgent: async (agentId) => {
    await AgentsService.delete(agentId)
    set((state) => ({ agents: state.agents.filter((agent) => agent.id !== agentId) }))
  },
}))
